import { JSX, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header1 from "../components/default-header";
import DefaultFooter from "../components/default-footer";
import { API_URL, Back_url } from "../config";
import Header from "../components/header";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CarPhoto {
  id: number;
  path: string;
  is_main: boolean;
  url?: string;
}

interface Car {
  id: number;
  name: string;
  model: string;
  type: string;
  number: string;
  engine_capacity: string;
  color: string;
  registration_year: string;
  price_per_day: string;
  price_per_month: string;
  price_per_year: string;
  is_available_for_rent: boolean;
  is_in_maintenance: boolean;
  photos: CarPhoto[];
}

type RateView = "day" | "month" | "year";
type Step = 1 | 2 | 3 | 4 | 5;
type Protection = "basic" | "full";
type ChecklistTab = "time" | "bring" | "deposit";

interface ExtraDef {
  id: string;
  label: string;
  sub: string;
  price: number;
  icon: JSX.Element;
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

const Icon = {
  Seat: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 17v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2" />
      <path d="M5 17V9a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Gear: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Fuel: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 22h11" /><path d="M6 18V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v12" />
      <path d="M14 9h1a2 2 0 0 1 2 2v3a1.5 1.5 0 0 0 3 0V8l-3-3" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Shield: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Cross: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Chevron: ({ dir = "left" }: { dir?: "left" | "right" }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points={dir === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
    </svg>
  ),
  Clock: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" />
    </svg>
  ),
  IdCard: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="5" width="20" height="14" rx="2" /><circle cx="8" cy="12" r="2" />
      <path d="M14 10h4M14 14h4" />
    </svg>
  ),
  Card: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
    </svg>
  ),
  UserPlus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </svg>
  ),
  Baby: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="7" r="4" /><path d="M5 21v-3a7 7 0 0 1 14 0v3" /><path d="M9 21v-4M15 21v-4" />
    </svg>
  ),
  Nav: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="3 11 22 2 13 21 11 13 3 11" />
    </svg>
  ),
  Plus: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Minus: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Info: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="11" x2="12" y2="16" /><circle cx="12" cy="8" r="0.5" fill="currentColor" />
    </svg>
  ),
};

const CarPlaceholderLarge = () => (
  <svg width="140" height="140" viewBox="0 0 200 200" fill="none" aria-hidden="true">
    <path
      d="M45 115 L60 85 C65 75 75 70 90 70 L125 70 C140 70 150 78 158 88 L168 105 C173 107 175 111 175 116 V125 C175 128 172 130 168 130 H162 C162 122 154 115 145 115 C136 115 128 122 128 130 H72 C72 122 64 115 55 115 C46 115 38 122 38 130 H32 C28 130 25 128 25 125 V118 C25 115 28 112 32 112 Z"
      fill="var(--teal-d)" fillOpacity="0.06" stroke="var(--gold)" strokeWidth="1.5"
    />
    <circle cx="55" cy="130" r="9" stroke="var(--gold)" strokeWidth="2" fill="var(--cream)" />
    <circle cx="145" cy="130" r="9" stroke="var(--gold)" strokeWidth="2" fill="var(--cream)" />
  </svg>
);

// Déplacer todayStr et fmtDate AVANT leur utilisation
const todayStr = () => new Date().toISOString().split("T")[0];
const fmtDate = (d: string) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const CarRentalDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [checklistTab, setChecklistTab] = useState<ChecklistTab>("time");

  const [rateView, setRateView] = useState<RateView>("day");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [pickupTime, setPickupTime] = useState("10:00");

  const [extraQty, setExtraQty] = useState<Record<string, number>>({});
  const [protection, setProtection] = useState<Protection | null>(null);

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [docType, setDocType] = useState("identity_card");
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");

  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const PERKS = [
    t("perk1"),
    t("perk2"),
    t("perk3"),
    t("perk4"),
    t("perk5"),
  ];

  const EXTRAS: ExtraDef[] = [
    { id: "driver", label: t("extraDriver"), sub: t("extraDriverSub"), price: 5, icon: <Icon.UserPlus /> },
    { id: "childseat", label: t("extraChildseat"), sub: t("extraChildseatSub"), price: 8, icon: <Icon.Baby /> },
    { id: "boosterseat", label: t("extraBoosterseat"), sub: t("extraBoosterseatSub"), price: 8, icon: <Icon.Baby /> },
    { id: "infantseat", label: t("extraInfantseat"), sub: t("extraInfantseatSub"), price: 8, icon: <Icon.Baby /> },
    { id: "gps", label: t("extraGps"), sub: t("extraGpsSub"), price: 6, icon: <Icon.Nav /> },
  ];

  const PROTECTION_ROWS = [
    { label: t("protectTheft"), basic: t("protectTheftBasic"), full: t("protectTheftFull") },
    { label: t("protectBodywork"), basic: t("protectBodyworkBasic"), full: t("protectBodyworkFull") },
    { label: t("protectOtherDamage"), basic: t("protectOtherDamageBasic"), full: t("protectOtherDamageFull"), basicBad: true },
    { label: t("protectBreakdown"), basic: t("protectBreakdownBasic"), full: t("protectBreakdownFull"), basicBad: true },
    { label: t("protectOtherCosts"), basic: t("protectOtherCostsBasic"), full: t("protectOtherCostsFull"), basicBad: true, fullBad: true },
  ];

  const STEPS: { n: Step; label: string }[] = [
    { n: 1, label: t("stepDates") },
    { n: 2, label: t("stepExtras") },
    { n: 3, label: t("stepProtection") },
    { n: 4, label: t("stepDetails") },
    { n: 5, label: t("stepReview") },
  ];

  useEffect(() => {
    const fetchCar = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await axios.get(`${API_URL}/cars-rental/${id}`);
        setCar(response.data.data ?? response.data);
      } catch (err) {
        console.error("Error fetching car:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCar();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem("token_customer_silverjarz");
      if (!token) return;

      try {
        const response = await axios.get(`${API_URL}/customer/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const userData = response.data.data ?? response.data;
        
        if (userData) {
          if (userData.firstname) setFirstname(userData.firstname);
          if (userData.lastname) setLastname(userData.lastname);
          if (userData.email) setEmail(userData.email);
          if (userData.phone) setPhone(userData.phone || "");
          if (userData.address) setAddress(userData.address || "");
        }
      } catch (err) {
        console.error("Erreur JWT / Token invalide :", err);
      }
    };

    fetchUserProfile();
  }, []);

  const duration = useMemo(() => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 1 ? diffDays : 1;
  }, [startDate, endDate]);

  const photos = car?.photos?.length
    ? [...car.photos].sort((a, b) => Number(b.is_main) - Number(a.is_main))
    : [];

  const unitPrice = useMemo(() => {
    if (!car) return 0;
    const map = { day: car.price_per_day, month: car.price_per_month, year: car.price_per_year };
    return parseFloat(map[rateView]) || 0;
  }, [car, rateView]);

  const unitLabel = { day: t("day"), month: t("month"), year: t("year") }[rateView];
  const basePrice = useMemo(() => unitPrice * duration, [unitPrice, duration]);
  const protectionCost = useMemo(() => (protection === "full" ? +(basePrice * 0.35).toFixed(2) : 0), [protection, basePrice]);
  const extrasList = useMemo(
    () => EXTRAS.filter((e) => (extraQty[e.id] || 0) > 0).map((e) => ({ ...e, qty: extraQty[e.id] })),
    [extraQty]
  );
  const extrasTotal = useMemo(() => extrasList.reduce((s, e) => s + e.price * e.qty, 0), [extrasList]);
  const payNow = +(basePrice + protectionCost).toFixed(2);
  const grandTotal = +(payNow + extrasTotal).toFixed(2);

  const setExtra = (id: string, delta: number) =>
    setExtraQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + delta) }));

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert(t("linkCopied"));
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneValid = /^\+?[0-9\s\-()]{7,15}$/.test(phone);

  const step1Valid = !!startDate && !!endDate && duration >= 1;
  const step3Valid = protection !== null;
  const step4Valid =
    !!firstname.trim() &&
    !!lastname.trim() &&
    emailValid &&
    phoneValid &&
    !!address.trim() &&
    !!insuranceFile &&
    !fileError;

  const goNext = () => setStep((s) => (Math.min(5, s + 1) as Step));
  const goBack = () => setStep((s) => (Math.max(1, s - 1) as Step));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setInsuranceFile(null);
      setFileError("");
      return;
    }
    const allowedExtensions = /(\.pdf|\.doc|\.docx|\.jpeg|\.jpg|\.png)$/i;
    if (!allowedExtensions.exec(file.name)) {
      setFileError(t("fileUnsupported"));
      setInsuranceFile(null);
    } else {
      setFileError("");
      setInsuranceFile(file);
    }
  };

  const submitBooking = async () => {
    if (!car) return;
    try {
      setSubmitting(true);
      setSubmitError("");

      const extrasNote = extrasList.length
        ? `${t("extrasLabel")}: ${extrasList.map((e) => `${e.label} x${e.qty}`).join(", ")}.`
        : `${t("extrasLabel")}: ${t("none")}.`;
      const protectionNote = `${t("protectionLabel")}: ${protection === "full" ? t("fullProtection") : t("basicProtection")}.`;
      const composedNotes = [notes.trim(), protectionNote, extrasNote].filter(Boolean).join(" ");

      const formData = new FormData();
      formData.append("car_id", String(car.id));
      formData.append("email", email);
      formData.append("firstname", firstname);
      formData.append("lastname", lastname);
      formData.append("phone", phone);
      formData.append("address", address);
      formData.append("new_price", grandTotal.toFixed(2));
      formData.append("duration", String(duration));
      formData.append("rate_type", rateView);
      formData.append("booking_date", startDate);
      formData.append("end_date", endDate);
      formData.append("document_type", docType);
      formData.append("notes", composedNotes);
      if (insuranceFile) {
        formData.append("insurance_document", insuranceFile);
      }

      const token = localStorage.getItem("token_customer_silverjarz");
      
      const res = await axios.post(`${API_URL}/booking-cars`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      
      const bookingId = res.data?.data?.id;
      navigate(`/car-rental/confirmation/${bookingId}`, {
        state: { booking: res.data?.data, car },
      });
    } catch (err: any) {
      console.error("Booking error:", err);
      const apiErrors = err?.response?.data?.errors;
      const firstError = apiErrors ? (Object.values(apiErrors)[0] as string[]) : null;
      setSubmitError(firstError?.[0] || err?.response?.data?.message || t("bookingError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

        :root {
          --teal: #24424f; --teal-d: #142832; --teal-l: #3c6474;
          --gold: #cdb573; --gold-l: #f1e9c8; --gold-d: #a3893f;
          --cream: #faf7f0; --paper: #ffffff; --mist: #efeadf; --line: rgba(20,40,50,.11);
          --silver: #7c8b93; --ink: #16262d;
          --ok: #2f7d5e; --ok-bg: #e4f1ea; --bad: #b0483c; --bad-bg: #f7e4e0;
        }
        .crd-page { font-family: 'DM Sans', sans-serif; background: var(--cream); min-height: 100vh; color: var(--ink); }
        .crd-page h1, .crd-page h2, .crd-page h3 { font-family: 'Playfair Display', serif; }
        .crd-mono { font-family: 'DM Mono', monospace; }
        .crd-wrap { max-width: 1180px; margin: 0 auto; padding: 130px 24px 90px; }

        .crd-breadcrumb { font-size: 12.5px; color: var(--silver); margin-bottom: 22px; }
        .crd-breadcrumb a { color: var(--teal-l); text-decoration: none; }
        .crd-breadcrumb a:hover { text-decoration: underline; }

        .crd-top-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; gap: 16px; flex-wrap: wrap; }
        .crd-actions-group { display: flex; gap: 10px; }
        .crd-action-btn {
          background: #fff; border: 1px solid var(--line); padding: 8px 14px; border-radius: 8px;
          font-size: 13px; font-weight: 600; color: var(--teal); cursor: pointer; display: flex; align-items: center; gap: 6px;
          transition: all .2s;
        }
        .crd-action-btn:hover { background: var(--teal); color: #fff; border-color: var(--teal); }

        .crd-layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 40px; align-items: start; }
        @media (max-width: 980px) { .crd-layout { grid-template-columns: 1fr; } }

        .crd-gallery-main {
          position: relative; height: 380px; border-radius: 14px; overflow: hidden;
          background: var(--mist); display: flex; align-items: center; justify-content: center;
          cursor: zoom-in; border: 1px solid var(--line);
        }
        .crd-gallery-main img { width: 100%; height: 100%; object-fit: cover; }
        .crd-badge {
          position: absolute; top: 16px; left: 16px;
          background: rgba(20,40,50,.85); color: #fff; font-size: 11px; font-weight: 600;
          letter-spacing: .5px; text-transform: uppercase; padding: 6px 14px; border-radius: 30px;
        }
        .crd-badge.unavailable { background: var(--bad); }
        .crd-thumbs { display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
        .crd-thumb {
          width: 76px; height: 58px; border-radius: 8px; overflow: hidden; cursor: pointer;
          border: 2px solid transparent; opacity: .6; transition: all .2s; background: var(--mist);
        }
        .crd-thumb.active { border-color: var(--gold-d); opacity: 1; }
        .crd-thumb img { width: 100%; height: 100%; object-fit: cover; }

        .crd-specs-grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
          margin: 26px 0; padding: 20px; background: #fff; border-radius: 12px; border: 1px solid var(--line);
        }
        @media (max-width: 560px) { .crd-specs-grid { grid-template-columns: repeat(2, 1fr); } }
        .crd-spec-item { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; }
        .crd-spec-icon { color: var(--gold-d); }
        .crd-spec-label { font-size: 10.5px; color: var(--silver); text-transform: uppercase; letter-spacing: .5px; }
        .crd-spec-value { font-size: 13.5px; font-weight: 600; color: var(--ink); }

        .crd-section-title { font-size: 18px; font-weight: 600; color: var(--ink); margin: 30px 0 14px; }
        .crd-perks { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .crd-perk { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: var(--teal-l); }
        .crd-perk-icon {
          width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
          background: var(--gold-l); color: var(--gold-d);
          display: flex; align-items: center; justify-content: center;
        }

        .crd-checklist { background: #fff; border-radius: 12px; border: 1px solid var(--line); margin-top: 30px; overflow: hidden; }
        .crd-checklist-tabs { display: flex; border-bottom: 1px solid var(--line); }
        .crd-checklist-tab {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px;
          padding: 15px 10px; font-size: 12.5px; font-weight: 600; color: var(--silver);
          background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; transition: all .2s;
        }
        .crd-checklist-tab.active { color: var(--teal); border-bottom-color: var(--gold-d); }
        .crd-checklist-body { padding: 20px 24px; font-size: 13.5px; color: var(--teal-l); line-height: 1.7; }
        .crd-checklist-body strong { color: var(--ink); }
        .crd-checklist-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
        .crd-checklist-list li { display: flex; align-items: center; gap: 9px; }
        .crd-checklist-list .tick { color: var(--ok); flex-shrink: 0; }

        .crd-rich-box { background: #fff; border-radius: 12px; padding: 24px; margin-top: 24px; border: 1px solid var(--line); }
        .crd-rich-box h3 { font-size: 16px; color: var(--ink); margin-bottom: 8px; }
        .crd-rich-box p, .crd-rich-box li { font-size: 13.5px; color: var(--teal-l); line-height: 1.6; }
        .crd-rich-box ul { padding-left: 18px; margin: 8px 0; }

        .crd-booking-card {
          background: #fff; border-radius: 16px; border: 1px solid var(--line);
          box-shadow: 0 16px 40px rgba(20,40,50,.09); overflow: hidden; position: sticky; top: 108px;
        }
        .crd-booking-head { padding: 20px 24px 18px; background: linear-gradient(135deg, var(--teal-d), var(--teal)); }
        .crd-booking-price { color: #fff; font-size: 24px; font-weight: 700; font-family: 'Playfair Display', serif; }
        .crd-booking-price span { font-size: 12px; font-weight: 500; color: var(--gold-l); font-family: 'DM Sans', sans-serif; }
        .crd-rate-toggle { display: flex; background: rgba(255,255,255,.12); border-radius: 30px; padding: 3px; margin-top: 12px; }
        .crd-rate-btn {
          flex: 1; font-size: 11px; font-weight: 600; color: #d8e1e5; background: transparent; border: none;
          border-radius: 20px; padding: 6px 4px; cursor: pointer; transition: all .2s;
        }
        .crd-rate-btn.active { background: var(--gold); color: var(--teal-d); }

        .crd-rail { display: flex; padding: 16px 22px 6px; background: var(--mist); gap: 2px; }
        .crd-rail-step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; position: relative; }
        .crd-rail-bar { width: 100%; height: 3px; border-radius: 3px; background: var(--line); }
        .crd-rail-step.done .crd-rail-bar, .crd-rail-step.active .crd-rail-bar { background: var(--gold-d); }
        .crd-rail-label { font-size: 9.5px; font-weight: 700; letter-spacing: .3px; text-transform: uppercase; color: var(--silver); text-align: center; }
        .crd-rail-step.active .crd-rail-label { color: var(--teal); }
        .crd-rail-step.done .crd-rail-label { color: var(--gold-d); }

        .crd-booking-body { padding: 20px 24px 6px; display: flex; flex-direction: column; gap: 12px; }
        .crd-step-title { font-size: 15.5px; font-weight: 700; color: var(--ink); margin: 0 0 2px; font-family: 'Playfair Display', serif; }
        .crd-step-sub { font-size: 12px; color: var(--silver); margin: -6px 0 6px; }

        .crd-field label {
          display: block; font-size: 11px; font-weight: 600; color: var(--teal-l);
          text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px;
        }
        .crd-field input, .crd-field select, .crd-field textarea {
          width: 100%; box-sizing: border-box; font-family: inherit; font-size: 13px; color: var(--ink);
          border: 1px solid var(--line); border-radius: 8px; padding: 9px 12px; transition: border-color .2s;
        }
        .crd-field input:focus, .crd-field select:focus, .crd-field textarea:focus { outline: none; border-color: var(--gold-d); }
        .crd-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .crd-field textarea { resize: vertical; min-height: 50px; }
        .crd-field-error { font-size: 10.5px; color: var(--bad); margin-top: 3px; }

        .crd-extra-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--line); }
        .crd-extra-row:last-child { border-bottom: none; }
        .crd-extra-icon {
          width: 34px; height: 34px; border-radius: 9px; background: var(--gold-l); color: var(--gold-d);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .crd-extra-info { flex: 1; min-width: 0; }
        .crd-extra-label { font-size: 13px; font-weight: 600; color: var(--ink); }
        .crd-extra-sub { font-size: 11.5px; color: var(--silver); margin-top: 1px; }
        .crd-extra-price { font-size: 11.5px; color: var(--teal-l); margin-top: 2px; }
        .crd-stepper { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .crd-stepper button {
          width: 26px; height: 26px; border-radius: 50%; border: 1px solid var(--line); background: #fff;
          color: var(--teal); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all .2s;
        }
        .crd-stepper button:hover:not(:disabled) { background: var(--teal); color: #fff; border-color: var(--teal); }
        .crd-stepper button:disabled { opacity: .3; cursor: not-allowed; }
        .crd-stepper span { width: 16px; text-align: center; font-weight: 700; font-size: 13px; }

        .crd-protect-table { border: 1px solid var(--line); border-radius: 10px; overflow: hidden; margin-bottom: 6px; }
        .crd-protect-head { display: grid; grid-template-columns: 1.2fr 1fr 1fr; background: var(--mist); }
        .crd-protect-head div { padding: 10px 8px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; color: var(--teal-l); text-align: center; }
        .crd-protect-head div:first-child { text-align: left; }
        .crd-protect-head div:last-child { color: var(--ok); }
        .crd-protect-row { display: grid; grid-template-columns: 1.2fr 1fr 1fr; border-top: 1px solid var(--line); }
        .crd-protect-row div { padding: 9px 8px; font-size: 11.5px; text-align: center; display: flex; align-items: center; justify-content: center; gap: 5px; color: var(--teal-l); }
        .crd-protect-row div:first-child { justify-content: flex-start; text-align: left; font-weight: 600; color: var(--ink); }
        .crd-protect-row .ok { color: var(--ok); } .crd-protect-row .bad { color: var(--bad); }
        .crd-protect-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .crd-protect-card {
          border: 2px solid var(--line); border-radius: 10px; padding: 12px; cursor: pointer; transition: all .2s; text-align: left;
        }
        .crd-protect-card.selected { border-color: var(--gold-d); background: var(--gold-l); }
        .crd-protect-card .name { font-size: 13px; font-weight: 700; color: var(--ink); }
        .crd-protect-card .price { font-size: 12px; color: var(--teal-l); margin-top: 2px; }

        .crd-ledger-wrap { padding: 10px 24px 0; }
        .crd-ledger {
          margin-top: 6px; padding: 16px 16px 14px; background: var(--cream); border-radius: 10px;
          border: 1px solid var(--line);
        }
        .crd-ledger-row { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px; color: var(--teal-l); padding: 3px 0; }
        .crd-ledger-row span:first-child { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .crd-ledger-row span:last-child { flex-shrink: 0; white-space: nowrap; }
        .crd-ledger-row.total { border-top: 1px dashed var(--line); margin-top: 6px; padding-top: 9px; font-weight: 700; color: var(--ink); font-size: 14px; }
        .crd-ledger-row.total .amt { color: var(--gold-d); }
        .crd-ledger-group-label { font-size: 10px; text-transform: uppercase; letter-spacing: .4px; color: var(--silver); font-weight: 700; margin: 8px 0 2px; }
        .crd-ledger-group-label:first-child { margin-top: 0; }

        .crd-cta-group { display: flex; gap: 8px; padding: 16px 24px 22px; }
        .crd-cta {
          flex: 1; background: var(--teal); color: #fff; border: none; border-radius: 8px;
          font-size: 13.5px; font-weight: 600; padding: 11px; cursor: pointer; transition: background .2s;
        }
        .crd-cta:hover:not(:disabled) { background: var(--teal-d); }
        .crd-cta:disabled { opacity: .5; cursor: not-allowed; }
        .crd-cta-secondary { background: transparent; color: var(--teal); border: 1px solid var(--line); flex: 0 0 auto; padding: 11px 16px; }

        .crd-trust { display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 11px; color: var(--silver); padding: 0 24px 18px; }

        .crd-review-block { border: 1px solid var(--line); border-radius: 10px; padding: 12px 14px; margin-bottom: 4px; }
        .crd-review-block h4 { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: .4px; color: var(--silver); font-family: 'DM Sans', sans-serif; font-weight: 700; }
        .crd-review-row { display: flex; justify-content: space-between; font-size: 12.5px; color: var(--teal-l); padding: 2px 0; }
        .crd-review-row b { color: var(--ink); }

        .crd-lightbox {
          position: fixed; inset: 0; background: rgba(10,20,26,.92); z-index: 1200;
          display: flex; align-items: center; justify-content: center; padding: 40px;
        }
        .crd-lightbox img { max-width: 100%; max-height: 100%; border-radius: 8px; }
        .crd-lightbox-close, .crd-lightbox-nav {
          position: absolute; background: rgba(255,255,255,.1); border: none; color: #fff;
          border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: background .2s;
        }
        .crd-lightbox-close { top: 20px; right: 20px; }
        .crd-lightbox-nav.prev { left: 20px; top: 50%; transform: translateY(-50%); }
        .crd-lightbox-nav.next { right: 20px; top: 50%; transform: translateY(-50%); }

        .crd-modal-overlay {
          position: fixed; inset: 0; background: rgba(20,40,50,.55); backdrop-filter: blur(3px);
          display: flex; align-items: center; justify-content: center; z-index: 1300; padding: 20px;
        }
        @keyframes crd-pulse {
          0% { opacity: 0.6; }
          50% { opacity: 0.9; }
          100% { opacity: 0.6; }
        }
        .crd-skeleton {
          background: #e2dcd0;
          border-radius: 8px;
          animation: crd-pulse 1.5s infinite ease-in-out;
        }
        .crd-modal { background: var(--cream); border-radius: 16px; max-width: 420px; width: 100%; padding: 32px 26px; text-align: center; }
        .crd-modal-icon { width: 52px; height: 52px; border-radius: 50%; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; }
        .crd-modal-icon.success { background: var(--gold-l); color: var(--gold-d); }
        .crd-modal-icon.error { background: var(--bad-bg); color: var(--bad); }
        .crd-modal h3 { font-size: 19px; color: var(--ink); margin: 0 0 8px; }
        .crd-modal p { font-size: 13px; color: var(--teal-l); line-height: 1.5; margin: 0 0 20px; }

        .crd-state { text-align: center; padding: 140px 24px; color: var(--silver); }
      `}</style>

      <div className="crd-page">
        <Header />

        {loading && (
          <div className="crd-wrap" style={{ opacity: 0.7, pointerEvents: 'none' }}>
            <div style={{ height: '20px', width: '250px' }} className="crd-skeleton" />
            <div className="crd-top-head" style={{ marginTop: '20px' }}>
              <div>
                <div style={{ height: '32px', width: '300px', marginBottom: '8px' }} className="crd-skeleton" />
                <div style={{ height: '16px', width: '180px' }} className="crd-skeleton" />
              </div>
            </div>
            <div className="crd-layout">
              <div>
                <div className="crd-gallery-main crd-skeleton" style={{ height: '380px' }} />
                <div className="crd-specs-grid crd-skeleton" style={{ height: '90px', marginTop: '26px' }} />
              </div>
              <div className="crd-booking-card crd-skeleton" style={{ height: '450px' }} />
            </div>
          </div>
        )}

        {!loading && (error || !car) && (
          <div className="crd-state">
            <p>{t("vehicleNotFound")}</p>
            <Link to="/car-rental" style={{ color: "var(--teal)" }}>← {t("backToFleet")}</Link>
          </div>
        )}

        {!loading && car && (
          <div className="crd-wrap">
            <div className="crd-breadcrumb">
              <Link to="/">{t("home")}</Link> / <Link to="/car-rental">{t("rentalFleet")}</Link> / {car.name}
            </div>

            <div className="crd-top-head">
              <div>
                <h1 style={{ fontSize: "28px", color: "var(--ink)", margin: 0 }}>{car.name}</h1>
                <p style={{ fontSize: "13.5px", color: "var(--silver)", margin: "4px 0 0" }}>
                  {car.model} · {car.color} · {t("reg")}. {car.registration_year}
                </p>
              </div>
              <div className="crd-actions-group">
                <button className="crd-action-btn" onClick={() => setIsSaved(!isSaved)}>
                  {isSaved ? "❤️ " + t("saved") : "🤍 " + t("save")}
                </button>
                <button className="crd-action-btn" onClick={handleShare}>🔗 {t("share")}</button>
              </div>
            </div>

            <div className="crd-layout">
              {/* LEFT COLUMN */}
              <div>
                <div className="crd-gallery-main" onClick={() => photos.length && setLightboxOpen(true)}>
                  {(!car.is_available_for_rent || car.is_in_maintenance) && (
                    <span className="crd-badge unavailable">{t("currentlyUnavailable")}</span>
                  )}
                  {photos.length ? (
                    <img src={`${Back_url}/${photos[activePhoto].path}`} alt={car.name} />
                  ) : (
                    <CarPlaceholderLarge />
                  )}
                </div>

                {photos.length > 1 && (
                  <div className="crd-thumbs">
                    {photos.map((p, i) => (
                      <div key={p.id} className={`crd-thumb ${i === activePhoto ? "active" : ""}`} onClick={() => setActivePhoto(i)}>
                        <img src={`${Back_url}/${p.path}`} alt="" />
                      </div>
                    ))}
                  </div>
                )}

                <div className="crd-specs-grid">
                  <div className="crd-spec-item">
                    <span className="crd-spec-icon"><Icon.Seat /></span>
                    <span className="crd-spec-label">{t("seats")}</span>
                    <span className="crd-spec-value">{car.number}</span>
                  </div>
                  <div className="crd-spec-item">
                    <span className="crd-spec-icon"><Icon.Gear /></span>
                    <span className="crd-spec-label">{t("transmission")}</span>
                    <span className="crd-spec-value" style={{ textTransform: "capitalize" }}>{car.type}</span>
                  </div>
                  <div className="crd-spec-item">
                    <span className="crd-spec-icon"><Icon.Fuel /></span>
                    <span className="crd-spec-label">{t("engine")}</span>
                    <span className="crd-spec-value">{car.engine_capacity}</span>
                  </div>
                  <div className="crd-spec-item">
                    <span className="crd-spec-icon"><Icon.Calendar /></span>
                    <span className="crd-spec-label">{t("regYear")}</span>
                    <span className="crd-spec-value">{car.registration_year}</span>
                  </div>
                </div>

                <h2 className="crd-section-title">{t("whatsIncluded")}</h2>
                <div className="crd-perks">
                  {PERKS.map((perk) => (
                    <div className="crd-perk" key={perk}>
                      <span className="crd-perk-icon"><Icon.Check /></span>
                      {perk}
                    </div>
                  ))}
                </div>

                <h2 className="crd-section-title">{t("pickupChecklist")}</h2>
                <div className="crd-checklist">
                  <div className="crd-checklist-tabs">
                    <button className={`crd-checklist-tab ${checklistTab === "time" ? "active" : ""}`} onClick={() => setChecklistTab("time")}>
                      <Icon.Clock /> {t("arriveOnTime")}
                    </button>
                    <button className={`crd-checklist-tab ${checklistTab === "bring" ? "active" : ""}`} onClick={() => setChecklistTab("bring")}>
                      <Icon.IdCard /> {t("whatToBring")}
                    </button>
                    <button className={`crd-checklist-tab ${checklistTab === "deposit" ? "active" : ""}`} onClick={() => setChecklistTab("deposit")}>
                      <Icon.Card /> {t("refundableDeposit")}
                    </button>
                  </div>
                  <div className="crd-checklist-body">
                    {checklistTab === "time" && (
                      <p>
                        {t("arriveOnTimeText")} <strong>{pickupTime}</strong> {t("on")} <strong>{fmtDate(startDate)}</strong>.
                      </p>
                    )}
                    {checklistTab === "bring" && (
                      <ul className="crd-checklist-list">
                        <li><span className="tick"><Icon.Check /></span> {t("bringPassport")}</li>
                        <li><span className="tick"><Icon.Check /></span> {t("bringLicence")}</li>
                        <li><span className="tick"><Icon.Check /></span> {t("bringCreditCard")}</li>
                      </ul>
                    )}
                    {checklistTab === "deposit" && (
                      <p>
                        {t("depositText")} <strong>{t("acceptedCards")}</strong> {t("acceptedCardsList")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="crd-rich-box">
                  <h3>{t("rentalGuidelines")}</h3>
                  <p>{t("rentalGuidelinesText")}</p>
                  <ul>
                    <li><strong>{t("mandatoryDocs")}:</strong> {t("mandatoryDocsText")}</li>
                    <li><strong>{t("fuelLevel")}:</strong> {t("fuelLevelText")}</li>
                    <li><strong>{t("support")}:</strong> {t("supportText")}</li>
                  </ul>
                </div>
              </div>

              {/* RIGHT COLUMN: booking wizard */}
              <div className="crd-booking-card">
                <div className="crd-booking-head">
                  <div className="crd-booking-price">
                    {unitPrice.toFixed(2)} <span>OMR / {unitLabel}</span>
                  </div>
                  <div className="crd-rate-toggle">
                    {(["day", "month", "year"] as RateView[]).map((r) => (
                      <button key={r} className={`crd-rate-btn ${rateView === r ? "active" : ""}`} onClick={() => setRateView(r)}>
                        {r === "day" ? t("daily") : r === "month" ? t("monthly") : t("yearly")}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="crd-rail">
                  {STEPS.map((s) => (
                    <div key={s.n} className={`crd-rail-step ${step === s.n ? "active" : step > s.n ? "done" : ""}`}>
                      <div className="crd-rail-bar" />
                      <div className="crd-rail-label">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="crd-booking-body">
                  {/* STEP 1 — Dates */}
                  {step === 1 && (
                    <>
                      <p className="crd-step-title">{t("whenDoYouNeedIt")}</p>
                      <p className="crd-step-sub">{t("pickYourDates")}</p>
                      <div className="crd-field-row">
                        <div className="crd-field">
                          <label>{t("startDate")}</label>
                          <input type="date" min={todayStr()} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        <div className="crd-field">
                          <label>{t("endDate")}</label>
                          <input type="date" min={startDate} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>
                      </div>
                      <div className="crd-field-row">
                        <div className="crd-field">
                          <label>{t("pickupTime")}</label>
                          <input type="time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} />
                        </div>
                        <div className="crd-field">
                          <label>{t("numberOf")} {unitLabel}{t("s")}</label>
                          <input type="number" readOnly value={duration} style={{ backgroundColor: '#f4f1eb' }} />
                        </div>
                      </div>
                    </>
                  )}

                  {/* STEP 2 — Extras */}
                  {step === 2 && (
                    <>
                      <p className="crd-step-title">{t("addExtras")}</p>
                      <p className="crd-step-sub">{t("addExtrasSub")}</p>
                      {EXTRAS.map((ex) => (
                        <div className="crd-extra-row" key={ex.id}>
                          <div className="crd-extra-icon">{ex.icon}</div>
                          <div className="crd-extra-info">
                            <div className="crd-extra-label">{ex.label}</div>
                            <div className="crd-extra-sub">{ex.sub}</div>
                            <div className="crd-extra-price">{ex.price.toFixed(2)} OMR {t("perRental")}</div>
                          </div>
                          <div className="crd-stepper">
                            <button onClick={() => setExtra(ex.id, -1)} disabled={!extraQty[ex.id]}><Icon.Minus /></button>
                            <span>{extraQty[ex.id] || 0}</span>
                            <button onClick={() => setExtra(ex.id, 1)}><Icon.Plus /></button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* STEP 3 — Protection */}
                  {step === 3 && (
                    <>
                      <p className="crd-step-title">{t("protectionTitle")}</p>
                      <p className="crd-step-sub">{t("protectionSub")}</p>
                      <div className="crd-protect-table">
                        <div className="crd-protect-head">
                          <div>{t("whatsCovered")}</div><div>{t("basic")}</div><div>{t("fullProtection")}</div>
                        </div>
                        {PROTECTION_ROWS.map((r) => (
                          <div className="crd-protect-row" key={r.label}>
                            <div>{r.label}</div>
                            <div className={r.basicBad ? "bad" : "ok"}>{r.basicBad ? <Icon.Cross /> : <Icon.Check />} {r.basic}</div>
                            <div className={r.fullBad ? "bad" : "ok"}>{r.fullBad ? <Icon.Cross /> : <Icon.Check />} {r.full}</div>
                          </div>
                        ))}
                      </div>
                      <div className="crd-protect-cards">
                        <div className={`crd-protect-card ${protection === "basic" ? "selected" : ""}`} onClick={() => setProtection("basic")}>
                          <div className="name">{t("basic")}</div>
                          <div className="price">{t("includedNoCost")}</div>
                        </div>
                        <div className={`crd-protect-card ${protection === "full" ? "selected" : ""}`} onClick={() => setProtection("full")}>
                          <div className="name">{t("fullProtection")}</div>
                          <div className="price">+{(basePrice * 0.35).toFixed(2)} OMR</div>
                        </div>
                      </div>
                      {!protection && <div className="crd-field-error">{t("selectProtection")}</div>}
                    </>
                  )}

                  {/* STEP 4 — Details */}
                  {step === 4 && (
                    <>
                      <p className="crd-step-title">{t("mainDriverDetails")}</p>
                      <p className="crd-step-sub">{t("allFieldsMandatory")}</p>
                      <div className="crd-field-row">
                        <div className="crd-field">
                          <label>{t("firstName")} *</label>
                          <input value={firstname} onChange={(e) => setFirstname(e.target.value)} placeholder={t("firstNamePlaceholder")} />
                        </div>
                        <div className="crd-field">
                          <label>{t("lastName")} *</label>
                          <input value={lastname} onChange={(e) => setLastname(e.target.value)} placeholder={t("lastNamePlaceholder")} />
                        </div>
                      </div>
                      <div className="crd-field-row">
                        <div className="crd-field">
                          <label>{t("email")} *</label>
                          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("emailPlaceholder")} />
                          {email && !emailValid && <div className="crd-field-error">{t("invalidEmail")}</div>}
                        </div>
                        <div className="crd-field">
                          <label>{t("phone")} *</label>
                          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("phonePlaceholder")} />
                          {phone && !phoneValid && <div className="crd-field-error">{t("invalidPhone")}</div>}
                        </div>
                      </div>
                      <div className="crd-field">
                        <label>{t("address")} *</label>
                        <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t("addressPlaceholder")} />
                      </div>
                      <div className="crd-field-row">
                        <div className="crd-field">
                          <label>{t("documentType")} *</label>
                          <select value={docType} onChange={(e) => setDocType(e.target.value)}>
                            <option value="identity_card">{t("identityCard")}</option>
                            <option value="license_driver">{t("driversLicense")}</option>
                            <option value="passport">{t("passport")}</option>
                            <option value="other">{t("other")}</option>
                          </select>
                        </div>
                        <div className="crd-field">
                          <label>{t("uploadDocument")} *</label>
                          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileChange} />
                        </div>
                      </div>
                      {fileError && <div className="crd-field-error">{fileError}</div>}
                      <small style={{ fontSize: "11px", color: "var(--silver)" }}>
                        {insuranceFile ? `${t("selected")}: ${insuranceFile.name}` : t("acceptedFormats")}
                      </small>
                      <div className="crd-field">
                        <label>{t("notesOptional")}</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("notesPlaceholder")} />
                      </div>
                    </>
                  )}

                  {/* STEP 5 — Review */}
                  {step === 5 && (
                    <>
                      <p className="crd-step-title">{t("reviewConfirm")}</p>
                      <p className="crd-step-sub">{t("reviewSub")}</p>

                      <div className="crd-review-block">
                        <h4>{t("vehicleDates")}</h4>
                        <div className="crd-review-row"><span>{t("vehicle")}</span><b>{car.name}</b></div>
                        <div className="crd-review-row"><span>{t("pickup")}</span><b>{fmtDate(startDate)}, {pickupTime}</b></div>
                        <div className="crd-review-row"><span>{t("dropoff")}</span><b>{fmtDate(endDate)}</b></div>
                        <div className="crd-review-row"><span>{t("length")}</span><b>{duration} {unitLabel}(s)</b></div>
                      </div>

                      <div className="crd-review-block">
                        <h4>{t("driver")}</h4>
                        <div className="crd-review-row"><span>{t("name")}</span><b>{firstname} {lastname}</b></div>
                        <div className="crd-review-row"><span>{t("contact")}</span><b>{email}</b></div>
                        <div className="crd-review-row"><span>{t("document")}</span><b style={{ textTransform: "capitalize" }}>{docType.replace("_", " ")}</b></div>
                      </div>

                      {extrasList.length > 0 && (
                        <div className="crd-review-block">
                          <h4>{t("extras")}</h4>
                          {extrasList.map((e) => (
                            <div className="crd-review-row" key={e.id}>
                              <span>{e.label} × {e.qty}</span><b>{(e.price * e.qty).toFixed(2)} OMR</b>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="crd-review-block">
                        <h4>{t("protection")}</h4>
                        <div className="crd-review-row">
                          <span>{protection === "full" ? t("fullProtection") : t("basicIncluded")}</span>
                          <b>{protection === "full" ? `${protectionCost.toFixed(2)} OMR` : "0.00 OMR"}</b>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Live price ledger */}
                <div className="crd-ledger-wrap">
                  <div className="crd-ledger">
                    <div className="crd-ledger-group-label">{t("payNow")}</div>
                    <div className="crd-ledger-row"><span>{unitPrice.toFixed(2)} OMR × {duration} {unitLabel}(s)</span><span>{basePrice.toFixed(2)} OMR</span></div>
                    {protection === "full" && (
                      <div className="crd-ledger-row"><span>{t("fullProtection")}</span><span>{protectionCost.toFixed(2)} OMR</span></div>
                    )}
                    {extrasList.length > 0 && (
                      <>
                        <div className="crd-ledger-group-label">{t("payAtPickup")}</div>
                        {extrasList.map((e) => (
                          <div className="crd-ledger-row" key={e.id}><span>{e.label} × {e.qty}</span><span>{(e.price * e.qty).toFixed(2)} OMR</span></div>
                        ))}
                      </>
                    )}
                    <div className="crd-ledger-row total"><span>{t("total")}</span><span className="amt crd-mono">{grandTotal.toFixed(2)} OMR</span></div>
                  </div>
                </div>

                <div className="crd-cta-group">
                  {step > 1 && (
                    <button className="crd-cta crd-cta-secondary" onClick={goBack}><Icon.Chevron dir="left" /></button>
                  )}
                  {step < 5 && (
                    <button
                      className="crd-cta"
                      disabled={(step === 1 && !step1Valid) || (step === 3 && !step3Valid) || (step === 4 && !step4Valid)}
                      onClick={goNext}
                    >
                      {step === 1 ? t("nextExtras") : step === 2 ? t("nextProtection") : step === 3 ? t("nextDetails") : t("reviewBooking")} →
                    </button>
                  )}
                  {step === 5 && (
                    <button
                      className="crd-cta"
                      disabled={!car.is_available_for_rent || car.is_in_maintenance || submitting}
                      onClick={submitBooking}
                    >
                      {submitting ? t("submitting") : t("confirmBook")}
                    </button>
                  )}
                </div>

                <div className="crd-trust"><Icon.Shield /> {t("secureBooking")}</div>
              </div>
            </div>
          </div>
        )}

        <DefaultFooter />
      </div>

      {lightboxOpen && photos.length > 0 && (
        <div className="crd-lightbox" onClick={() => setLightboxOpen(false)}>
          <button className="crd-lightbox-close" onClick={() => setLightboxOpen(false)}>✕</button>
          {photos.length > 1 && (
            <>
              <button className="crd-lightbox-nav prev" onClick={(e) => { e.stopPropagation(); setActivePhoto((p) => (p - 1 + photos.length) % photos.length); }}>
                <Icon.Chevron dir="left" />
              </button>
              <button className="crd-lightbox-nav next" onClick={(e) => { e.stopPropagation(); setActivePhoto((p) => (p + 1) % photos.length); }}>
                <Icon.Chevron dir="right" />
              </button>
            </>
          )}
          <img src={photos[activePhoto].url || `${Back_url}/storage/${photos[activePhoto].path}`} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {submitError && (
        <div className="crd-modal-overlay" onClick={() => setSubmitError("")}>
          <div className="crd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crd-modal-icon error">✕</div>
            <h3>{t("bookingFailed")}</h3>
            <p>{submitError}</p>
            <button className="crd-cta" onClick={() => setSubmitError("")}>{t("tryAgain")}</button>
          </div>
        </div>
      )}
    </>
  );
};

export default CarRentalDetail;